import { sum } from "lodash";
import { useMemo } from "react";

import { MODERNIZATION_MAX_YEAR } from "../configs/Modernization";
import { techDataActiveSections, techDataSections } from "../configs/TechnicalDataEnums";
import {
  allDataGroupedColumns,
  allGroupsNames,
  categoricalColumns,
  columnsAPINamesEnums,
  dataNeededColumns,
  FiltersNames,
  FinancialDataColsNamesEnums,
  financialDataGroupedColumns,
  getFinancialDataPrefix,
  getPortfolioColumnDisplayName,
  getRetrofitPrefix,
  infoBoxAndTableNeededColumns,
  numbersScalesTypes,
  parentDataGroupedColumns,
  portfolioScaleableColumns,
  RetrifitsColsNamesEnums,
  retrofitsGroupedColumns,
} from "../enums/AssetOverview/portfolio";
import {
  getTechDataPrefix,
  TechnicalDataColsNamesEnums,
  TechnicalDataHeatingCoolingSystemSummaryColsNamesEnums,
  TechnicalDataSummarySectionsNamesEnums,
  TechnicalDataWindowSummaryColsNamesEnums,
} from "../enums/AssetOverview/portfolioTechnicalDataPart";
import { valuesEnums } from "../enums/dynamicDashboard";
import { getSingleMeasureFinancialValuesTimeSeries } from "../functions/Modernization";
import { deleteKeysFromObjects, distinctFilter, doArraysIntersect } from "./dataManipulation";
import {
  getItemsEqualityCriteriaMapping,
  settingsOptions,
  summaryTypes,
  summaryTypesNames,
  unpivotData,
} from "./dynamicDashboard";
import { getBuildingGrossArea, perM2ValueToNormal } from "./general";
import { calculateInitialEnergySaving } from "./Modernization";

const getDataChildUniqueValues = (data, parentKey, childKey) => {
  const allValues = data?.reduce((total, curr) => {
    const currentValues = curr?.[parentKey]?.map((parent) => parent?.[childKey]) ?? [];
    return [...total, ...currentValues];
  }, []);
  const uniqueValues = [...new Set(allValues)];
  return uniqueValues;
};

export const getRetrofitKeyUniqueValues = (generalDashboardData, key) => {
  return getDataChildUniqueValues(generalDashboardData, columnsAPINamesEnums.RETROFITS, key);
};

export const useGetRetrofitValuesOptions = (generalDashboardData) => {
  const subComponentValues = useMemo(
    () => getRetrofitKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.RETROFITS__SUB_COMPONENT),
    [generalDashboardData]
  );
  const componentValues = useMemo(
    () => getRetrofitKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.RETROFITS__COMPONENT),
    [generalDashboardData]
  );
  const loanTypeValues = useMemo(
    () => getRetrofitKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.RETROFITS__LOAN_TYPE),
    [generalDashboardData]
  );
  const typeOfMeasureValues = useMemo(
    () => getRetrofitKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.RETROFITS__TYPE_OF_MESSURE),
    [generalDashboardData]
  );
  return {
    componentValues,
    loanTypeValues,
    subComponentValues,
    typeOfMeasureValues,
  };
};

export const getTechnicalDataKeyUniqueValues = (generalDashboardData, key) => {
  return getDataChildUniqueValues(generalDashboardData, columnsAPINamesEnums.TECHNICAL_DATA, key);
};

export const useGetTechnicalDataValuesOptions = (generalDashboardData) => {
  const objectTypeValues = useMemo(
    () => getTechnicalDataKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.TECHNICAL_DATA__SUB_COMPONENT),
    [generalDashboardData]
  );
  const colorValues = useMemo(
    () => getTechnicalDataKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.TECHNICAL_DATA__COLOR),
    [generalDashboardData]
  );
  const windowTypeValues = useMemo(
    () => getTechnicalDataKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_TYPE),
    [generalDashboardData]
  );
  const energySourceValues = useMemo(
    () => getTechnicalDataKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.TECHNICAL_DATA__ENERGY_SOURCE),
    [generalDashboardData]
  );
  const systemTypeValues = useMemo(
    () => getTechnicalDataKeyUniqueValues(generalDashboardData, columnsAPINamesEnums.TECHNICAL_DATA__SYSTEM_TYPE),
    [generalDashboardData]
  );

  return { colorValues, energySourceValues, objectTypeValues, systemTypeValues, windowTypeValues };
};

export const useGetDataColumnsOptions = (data) => {
  const dataColumnsOptions = useMemo(() => {
    let dataColumns = [],
      numericalColumns = [];
    if (data?.length) {
      dataColumns = Object.keys(data[0]);
    }

    const summarySections = Object.values(TechnicalDataSummarySectionsNamesEnums);
    const summaryCols = [
      ...Object.values(TechnicalDataWindowSummaryColsNamesEnums),
      ...Object.values(TechnicalDataHeatingCoolingSystemSummaryColsNamesEnums),
    ];
    const techDataSummaryRelatedCols = [...summarySections, ...summaryCols];
    const techDataNonSummaryCols = Object.values(TechnicalDataColsNamesEnums).filter(
      (col) => !techDataSummaryRelatedCols?.includes(col)
    );

    const allDataColumns = [
      ...(dataColumns ?? []),
      ...Object.values(FinancialDataColsNamesEnums),
      ...Object.values(RetrifitsColsNamesEnums),
      ...Object.values(techDataNonSummaryCols),
    ].filter(distinctFilter);
    numericalColumns = allDataColumns.filter((col) => !categoricalColumns.includes(col));

    const allDataColumnsDisplayOptions = allDataColumns?.map((colName) => getPortfolioColumnDisplayName({ colName }));

    const allDataGroupedColumnsOptions = Object.keys(allDataGroupedColumns);
    const allDataColumnsAndGroupedColumnsOptions = [...allDataColumns, ...allDataGroupedColumnsOptions];
    const allDataColumnsAndGroupedColumnsDisplayOptions = allDataColumnsAndGroupedColumnsOptions?.map((colName) =>
      getPortfolioColumnDisplayName({ colName })
    );

    const summarySectionsDisplayOptions = summarySections?.map((colName) => getPortfolioColumnDisplayName({ colName }));
    return {
      allDataColumns,
      allDataColumnsAndGroupedColumnsDisplayOptions,
      allDataColumnsAndGroupedColumnsOptions,
      allDataColumnsDisplayOptions,
      numericalColumns,
      summarySections,
      summarySectionsDisplayOptions,
    };
  }, [data]);

  return dataColumnsOptions;
};

const filterDataByChildCategoricalKey = (data, parentKey, childKey, selectedValues) => {
  if (!selectedValues?.length || !childKey || !parentKey) return data;
  else {
    return data?.filter((building) => {
      const dataKeyValues = building[parentKey]?.map((technicalData) => technicalData?.[childKey]);
      return dataKeyValues?.filter((value) => selectedValues?.includes(value))?.length > 0;
    });
  }
};

const dataAndChildsMultiSelectFilterFunctionByChildKey = (data, parentKey, childKey, selectedValues) => {
  if (!selectedValues?.length || !parentKey || !childKey) return data;

  const filteredData = filterDataByChildCategoricalKey(data, parentKey, childKey, selectedValues);
  const filteredDataWithFilteredChilds = filteredData?.map((building) => {
    const filteredChilds = building[parentKey]?.filter((child) => selectedValues?.includes(child?.[childKey]));
    return { ...building, [parentKey]: filteredChilds };
  });

  return filteredDataWithFilteredChilds;
};

export const dataMultiSelectFilterFunctionByRetrofitsKeys = (data, dataKey, selectedValues) => {
  return filterDataByChildCategoricalKey(data, columnsAPINamesEnums.RETROFITS, dataKey, selectedValues);
};

export const dataAndRetrofitsMultiSelectFilterFunctionByRetrofitsKeys = (data, dataKey, selectedValues) => {
  if (!selectedValues?.length || !dataKey) return data;

  const parentKey = columnsAPINamesEnums.RETROFITS;
  return dataAndChildsMultiSelectFilterFunctionByChildKey(data, parentKey, dataKey, selectedValues);
};

export const dataMultiSelectFilterFunctionByTechnicalDataKeys = (data, dataKey, selectedValues) => {
  if (!selectedValues?.length || !dataKey) return data;

  const parentKey = columnsAPINamesEnums.TECHNICAL_DATA;
  return filterDataByChildCategoricalKey(data, parentKey, dataKey, selectedValues);
};

export const dataFinancialDataRangeFilterFunctionByFinancialKeys = (data, dataKey, range) => {
  const min = range?.[0];
  const max = range?.[1];

  if (min == undefined || max == undefined) return data;

  return data?.map((building) => {
    const retrofits = building[columnsAPINamesEnums.RETROFITS];

    return {
      ...building,
      [columnsAPINamesEnums.RETROFITS]: retrofits?.map((retrofit) => {
        const filteredFinancialData = retrofit[columnsAPINamesEnums.FINANCIAL_DATA]?.filter((yearData) => {
          return yearData[dataKey] >= min && yearData[dataKey] <= max;
        });

        return { ...retrofit, [columnsAPINamesEnums.FINANCIAL_DATA]: filteredFinancialData };
      }),
    };
  });
};

export const dataFinancialDataMultiSelectFilterFunctionByFinancialGroups = (data, groupKey, selectedValues) => {
  const groupColoumns = allDataGroupedColumns[groupKey]?.columns;

  if (!selectedValues?.length || !groupColoumns?.length) return data;

  const columnsToRemove = groupColoumns?.filter((col) => !selectedValues?.includes(col));

  return data?.map((building) => {
    const retrofits = building[columnsAPINamesEnums.RETROFITS];

    return {
      ...building,
      [columnsAPINamesEnums.RETROFITS]: retrofits?.map((retrofit) => {
        const financialData = retrofit[columnsAPINamesEnums.FINANCIAL_DATA];
        const shreinkedFinancialData = deleteKeysFromObjects(financialData, columnsToRemove);
        return { ...retrofit, [columnsAPINamesEnums.FINANCIAL_DATA]: shreinkedFinancialData };
      }),
    };
  });
};

export const flattenPortfolioData3Level = ({ data }) => {
  const flattened = [];
  for (const row of data) {
    const retrofits = row[columnsAPINamesEnums.RETROFITS];

    if (!retrofits?.length) {
      // If no retrofits, include the row by itself
      flattened.push({ ...(row ?? {}) });
      continue;
    }

    for (const retrofit of retrofits) {
      const financialData = retrofit[columnsAPINamesEnums.FINANCIAL_DATA];

      if (!financialData?.length) {
        // If no financial data, include row + retrofit
        flattened.push({
          ...(retrofit ?? {}),
          ...(row ?? {}),
        });
        continue;
      }

      for (const singleYearFinancialData of financialData) {
        flattened.push({
          ...(singleYearFinancialData ?? {}),
          ...(retrofit ?? {}),
          ...(row ?? {}),
        });
      }
    }
  }

  const doubleFlattened = [];
  for (const row of flattened) {
    const technicalData = row[columnsAPINamesEnums.TECHNICAL_DATA];
    if (!technicalData?.length) {
      doubleFlattened.push({ ...(row ?? {}) });
      continue;
    }
    for (const technicalDataRow of technicalData) {
      doubleFlattened.push({
        ...(technicalDataRow ?? {}),
        ...(row ?? {}),
      });
    }
  }

  return doubleFlattened;
};

export const flattenPortfolioDataAcrossTechnicalDataLevels = ({ data }) => {
  const flattened = [];
  for (const row of data) {
    const technicalData = row[columnsAPINamesEnums.TECHNICAL_DATA];
    for (const technicalDataRow of technicalData) {
      flattened.push({
        ...(technicalDataRow ?? {}),
        ...(row ?? {}),
      });
    }
  }
  return flattened;
};

export const flattenPortfolioData3LevelMinimal = ({ data }) => {
  const flattened = [];
  for (const row of data) {
    const retrofits = row[columnsAPINamesEnums.RETROFITS];
    for (const retrofit of retrofits) {
      const financialData = retrofit[columnsAPINamesEnums.FINANCIAL_DATA];
      for (const singleYearFinancialData of financialData) {
        flattened.push({
          ...(singleYearFinancialData ?? {}),
          ...(retrofit ?? {}),
          ...(row ?? {}),
        });
      }
    }
  }

  return flattened;
};

export const flattenPortfolioData2LevelMinimal = ({ data }) => {
  const flattened = [];
  for (const row of data) {
    for (const retrofit of row[columnsAPINamesEnums.RETROFITS]) {
      flattened.push({
        ...(retrofit ?? {}),
        ...(row ?? {}),
      });
    }
  }

  return flattened;
};

const getVisibleSettingsValues = ({ allSettings, config, settingsNamesAndValues }) => {
  const visibleSettings = settingsNamesAndValues?.filter((nameAndValue) => {
    const setOptionsInSettings = allSettings(() => {}, config)?.filter(
      (setting) => nameAndValue.name === setting.filterName
    );
    return setOptionsInSettings?.some((setting) => setting?.visible?.());
  });

  const visibleSettingsValues = visibleSettings
    ?.map((setting) => setting.value)
    ?.reduce((acc, curr) => {
      return [...acc, ...(Array.isArray(curr) ? curr : [curr])];
    }, []);

  return visibleSettingsValues;
};

export const handleAdjustingAggregatedDataPointsFilterByGroupDataPoints = ({ data }) => {
  const aggregatedColumnsAdjustedValuesData = data?.map((building) => {
    const retrofits = building[columnsAPINamesEnums.RETROFITS];

    return {
      ...building,
      [columnsAPINamesEnums.RETROFITS]: retrofits?.map((retrofit) => {
        const financialData = retrofit[columnsAPINamesEnums.FINANCIAL_DATA];
        return {
          ...retrofit,
          [columnsAPINamesEnums.FINANCIAL_DATA]: financialData?.map((yearData) => {
            const adjusted = addAggregatedFinancialDataPointsToRecord({ row: yearData });
            return adjusted;
          }),
        };
      }),
    };
  });

  return aggregatedColumnsAdjustedValuesData;
};

export const handleNestingTransformation = ({
  allSettings,
  config,
  data,
  dataPoint,
  diagrams,
  partitions,
  stacks,
  xAxis,
  yAxis,
}) => {
  const settingsNamesAndValues = [
    { name: FiltersNames.DATAPOINT, value: dataPoint },
    { name: FiltersNames.DIAGRAMS_PARTITIONS, value: partitions },
    { name: FiltersNames.DIAGRAMS_STACKS, value: stacks },
    { name: FiltersNames.X_AXIS, value: xAxis },
    { name: FiltersNames.Y_AXIS, value: yAxis },
    { name: FiltersNames.DIAGRAMS_DIAGRAM_DATA_KEY, value: diagrams?.map((diagram) => diagram.dataKey) },
  ];
  const visibleSettingsValues = getVisibleSettingsValues({ allSettings, config, settingsNamesAndValues });

  if (doArraysIntersect(visibleSettingsValues, Object.values(FinancialDataColsNamesEnums))) {
    data = flattenPortfolioData3LevelMinimal({ data });
  } else if (doArraysIntersect(visibleSettingsValues, Object.values(RetrifitsColsNamesEnums))) {
    data = flattenPortfolioData2LevelMinimal({ data });
  }

  if (doArraysIntersect(visibleSettingsValues, Object.values(TechnicalDataColsNamesEnums))) {
    data = flattenPortfolioDataAcrossTechnicalDataLevels({ data });
  }

  return data;
};

export const handleScalingTransformation = ({ config, data, originalData }) => {
  let scaledData = [...data];
  const totalArea = sum(originalData?.map(getBuildingGrossArea));
  const denominator = config?.[FiltersNames.SCALING_TYPE] === numbersScalesTypes.TOTAL_AREA ? totalArea : 1;
  if ([numbersScalesTypes.NO_SCALE, numbersScalesTypes.TOTAL_AREA].includes(config?.[FiltersNames.SCALING_TYPE])) {
    scaledData = data?.map((row) => {
      const scaled = { ...row };
      Object.keys(row)
        ?.filter((col) => portfolioScaleableColumns.includes(col))
        ?.forEach((col) => {
          scaled[col] = (perM2ValueToNormal({ building: row, value: row[col] }) ?? 1) / (denominator ?? 1);
        });
      return scaled;
    });
  }
  return scaledData;
};

export const getSelectedGroup = ({ allSettings, config, dataPoint, diagrams, partitions, stacks, xAxis, yAxis }) => {
  const groupsSettingsNamesAndValues = [
    { name: FiltersNames.DIAGRAMS_PARTITIONS, value: partitions },
    { name: FiltersNames.DIAGRAMS_STACKS, value: stacks },
    { name: FiltersNames.X_AXIS, value: xAxis },
    { name: FiltersNames.Y_AXIS, value: yAxis },
  ];
  const visibleGroupsSettingsValues = getVisibleSettingsValues({
    allSettings,
    config,
    settingsNamesAndValues: groupsSettingsNamesAndValues,
  });

  const aggregationSettingsNamesAndValues = [
    { name: FiltersNames.DATAPOINT, value: dataPoint },
    { name: FiltersNames.DIAGRAMS_DIAGRAM_DATA_KEY, value: diagrams?.map((diagram) => diagram.dataKey) },
  ];
  const visibleAggregationSettingsValues = getVisibleSettingsValues({
    allSettings,
    config,
    settingsNamesAndValues: aggregationSettingsNamesAndValues,
  });

  let selectedGroup,
    selectedGroupName = null;
  for (const groupName in allDataGroupedColumns) {
    const group = allDataGroupedColumns[groupName];
    const aggregatedColumn = group.aggregatedColumn;

    if (
      visibleAggregationSettingsValues?.includes(aggregatedColumn) &&
      visibleGroupsSettingsValues?.includes(groupName)
    ) {
      selectedGroup = group;
      selectedGroupName = groupName;
      break;
    }
  }

  return { selectedGroup, selectedGroupName };
};

export const handleGroupingTransformation = ({
  allSettings,
  config,
  data,
  dataPoint,
  diagrams,
  partitions,
  stacks,
  xAxis,
  yAxis,
}) => {
  let groupedData = [...(data ?? [])];
  const { selectedGroup, selectedGroupName } = getSelectedGroup({
    allSettings,
    config,
    dataPoint,
    diagrams,
    partitions,
    stacks,
    xAxis,
    yAxis,
  });
  if (selectedGroup) {
    groupedData = unpivotData({
      columnsToUnpivot: selectedGroup.columns,
      data,
      newColumnName: selectedGroupName,
      valueName: selectedGroup.aggregatedColumn,
    });
  }
  return groupedData;
};

const undefineGroupsValuesInMapping = ({ equalityMapping }) => {
  const overriddenEqualityMapping = { ...equalityMapping };

  Object.entries(equalityMapping ?? {}).forEach(([settingName, settingValue]) => {
    if (allGroupsNames.includes(settingValue)) {
      overriddenEqualityMapping[settingName] = undefined;
    }
  });
  return overriddenEqualityMapping;
};

export const removeTheEffectOfGroupsFromConfig = ({ config = {} }) => {
  const { diagrams, ...rest } = config ?? {};
  const { partitions, stacks } = diagrams?.[0] ?? {};
  const equalityMapping = getItemsEqualityCriteriaMapping({ partitions, stacks, ...rest });
  const overriddenEqualityMapping = undefineGroupsValuesInMapping({ equalityMapping });

  const overriddenConfig = {
    ...config,
    ...overriddenEqualityMapping,
    diagrams: [
      {
        ...config?.diagrams?.[0],
        [settingsOptions.PARTITIONS]: overriddenEqualityMapping?.[settingsOptions.PARTITIONS],
        [settingsOptions.STACKS]: overriddenEqualityMapping?.[settingsOptions.STACKS],
      },
      ...(config?.diagrams?.slice(1) ?? []),
    ],
  };
  return overriddenConfig;
};

export const renameFinancialDataKeys = ({ row }) => {
  const renamed = {};
  Object.keys(row ?? {}).forEach((key) => {
    renamed[getFinancialDataPrefix(key)] = row[key];
  });
  return renamed;
};

export const addAggregatedFinancialDataPointsToRecord = ({ row }) => {
  const extended = { ...row };
  Object.values(financialDataGroupedColumns).forEach((group) => {
    const aggregatedColumn = group.aggregatedColumn;
    const columns = group.columns;
    extended[aggregatedColumn] = sum(columns?.map((col) => row[col] ?? 0) ?? []);
  });
  return extended;
};

export const refinePortfolioData = ({ data }) => {
  const mappedData = [];
  const mappedInfoBoxAndTableData = [];
  data?.forEach((row) => {
    const mappedDataRow = {};
    const mappedInfoBoxAndTableRow = {};
    Object.keys(row).forEach((dataKey) => {
      let value = row[dataKey];

      if (categoricalColumns.includes(dataKey)) {
        value = value ?? valuesEnums.NO_DATA;
      }
      if (infoBoxAndTableNeededColumns.includes(dataKey)) {
        mappedInfoBoxAndTableRow[dataKey] = value;
      }

      if (dataNeededColumns.includes(dataKey)) {
        mappedDataRow[dataKey] = value;
      }

      mappedDataRow[columnsAPINamesEnums.COUNT] = 1;
    });

    Object.values(parentDataGroupedColumns).forEach((group) => {
      const aggregatedColumn = group.aggregatedColumn;
      const columns = group.columns;
      mappedDataRow[aggregatedColumn] = sum(columns?.map((col) => row[col] ?? 0) ?? []);
    });

    let flatTechData = [];
    const techDataSectionsNames = Object.values(techDataActiveSections);
    techDataSectionsNames?.forEach((section) => {
      const sectionData = row[section]?.map((techDataRow) => {
        const rowRenamed = {};
        Object.keys(techDataRow ?? {}).forEach((key) => {
          rowRenamed[getTechDataPrefix(key)] = techDataRow[key];
        });
        return {
          ...rowRenamed,
          [columnsAPINamesEnums.TECHNICAL_DATA__SUB_COMPONENT]: section,
        };
      });
      flatTechData = flatTechData.concat(sectionData ?? []);
    });
    mappedDataRow[columnsAPINamesEnums.TECHNICAL_DATA] = flatTechData;

    mappedDataRow[columnsAPINamesEnums.RETROFITS] = mappedDataRow[columnsAPINamesEnums.RETROFITS]?.map((retrofit) => {
      const retrofitWithDetails = { ...retrofit, ...(retrofit?.retrofit_details ?? {}) };
      delete retrofitWithDetails?.["retrofit_details"];
      const retrofitDetailsRenamed = {};
      Object.keys(retrofitWithDetails ?? {}).forEach((key) => {
        retrofitDetailsRenamed[getRetrofitPrefix(key)] = retrofitWithDetails[key];
      });

      Object.values(retrofitsGroupedColumns).forEach((group) => {
        const aggregatedColumn = group.aggregatedColumn;
        const columns = group.columns;
        retrofitDetailsRenamed[aggregatedColumn] = sum(columns?.map((col) => retrofitDetailsRenamed[col] ?? 0) ?? []);
      });

      const financialData = getSingleMeasureFinancialValuesTimeSeries({
        endYear: MODERNIZATION_MAX_YEAR,
        initialValues: {
          foreignCapital: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__FOEIGN_CAPITAL],
          inflationRatePercentage: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__INFLATION_RATE],
          initialEnergySaving: calculateInitialEnergySaving(
            retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__HEAT_ENERGY_COST_SAVINGS],
            retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__ELECTRICITY_COST_SAVINGS]
          ),
          initialMaintenanceCosts: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__MAINTENANCE_COST],
          initialOtherSavings: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__OTHER_COST_SAVINGS],
          initialServicesCosts: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__SERVICES_COST],
          interestRatePercentage: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__INTEREST_RATE],
          loanRepaymentTime: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__REPAYMENT_TIME],
          ownCapital: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__OWN_CAPITAL],
          subsidies: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__SUBSIDIES],
        },
        startYear: retrofitDetailsRenamed[columnsAPINamesEnums.RETROFITS__YEAR],
      });

      return {
        ...retrofitDetailsRenamed,
        [columnsAPINamesEnums.FINANCIAL_DATA]: financialData?.map((yearData) => {
          const renamed = renameFinancialDataKeys({ row: yearData });
          const extended = addAggregatedFinancialDataPointsToRecord({ row: renamed });
          return extended;
        }),
      };
    });

    mappedDataRow[columnsAPINamesEnums.ENERGY_CONSUMPTION_RATIO] = row[columnsAPINamesEnums.BUILDING_TYPE_ID];

    mappedInfoBoxAndTableData.push(mappedInfoBoxAndTableRow);
    mappedData.push(mappedDataRow);
  });

  return { mappedData, mappedInfoBoxAndTableData };
};

export const summarizeChartData = ({ data, summaryItems }) => {
  const aggregatedData = {};
  if (!data?.length > 0 || !summaryItems?.length) return aggregatedData;

  summaryItems.forEach((summaryItem) => {
    const { name, type, ...rest } = summaryItem;
    const value = summaryTypes[type]?.aggregatorFunction?.({ data, ...rest });

    if (summaryTypes[type]?.displayFunction) {
      aggregatedData[name] = summaryTypes[type]?.displayFunction?.({ value });
    } else {
      aggregatedData[name] = value;
    }
  });
  return aggregatedData;
};

export const summarizeHeatingCoolingSystemData = ({ data, isHeating }) => {
  const heatingSystemSummaryItems = [
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__SYSTEM_TYPE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__AMOUNT_OF_DIFFERENT_SYSTEMS,
      type: summaryTypesNames.COUNT_UNIQUE_VALUES,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__AREA_COVERED,
      groupByColumn: columnsAPINamesEnums.TECHNICAL_DATA__SYSTEM_TYPE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__MAIN_SYSTEM_TYPE,
      type: summaryTypesNames.DOMINANT_AGGREGATED_CATEGORY,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__AREA_COVERED,
      groupByColumn: columnsAPINamesEnums.TECHNICAL_DATA__ENERGY_SOURCE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__MAIN_ENERGY_SOURCE,
      type: summaryTypesNames.DOMINANT_AGGREGATED_CATEGORY,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__AREA_COVERED,
      groupByColumn: columnsAPINamesEnums.TECHNICAL_DATA__PEAK_THERMAL_POWER,
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__MAIN_SYSTEM_THERMAL_POWER,
      type: summaryTypesNames.DOMINANT_AGGREGATED_CATEGORY,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__PEAK_THERMAL_POWER,
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__TOTAL_THERMAL_POWER,
      type: summaryTypesNames.TOTAL,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__INSTALLED,
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__INSTALLATION_YEARS,
      type: summaryTypesNames.RANGE,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__AREA_COVERED,
      filterFunction: (row) => row[columnsAPINamesEnums.TECHNICAL_DATA__MONITORING_INSTALLED],
      name: columnsAPINamesEnums.TECHNICAL_DATA__HEATING_SYSTEM_SUMMARY__MONITORING_SYSTEM_INSTALLED,
      type: summaryTypesNames.VALUE_COVERAGE,
    },
  ];

  const systemType = isHeating ? techDataSections.HEATING_SYSTEM : techDataSections.COOLING_SYSTEM;
  const filteredData = data?.filter((row) => row[columnsAPINamesEnums.TECHNICAL_DATA__SUB_COMPONENT] === systemType);
  return summarizeChartData({ data: filteredData, summaryItems: heatingSystemSummaryItems });
};

export const summarizeCoolingSystemData = ({ data }) => {
  return summarizeHeatingCoolingSystemData({ data, isHeating: false });
};

export const summarizeHeatingSystemData = ({ data }) => {
  return summarizeHeatingCoolingSystemData({ data, isHeating: true });
};

export const summarizeWindowData = ({ data }) => {
  const windowSummaryItems = [
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__NUMBER_OF_WINDOWS,
      name: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_SUMMARY__TOTAL_NUMBER_OF_WINDOWS,
      type: summaryTypesNames.TOTAL,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_TYPE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_SUMMARY__AMOUNT_OF_DIFFERENT_WINDOWS,
      type: summaryTypesNames.COUNT_UNIQUE_VALUES,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_TYPE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_SUMMARY__AMOUNT_OF_DIFFERENT_WINDOWS,
      type: summaryTypesNames.COUNT_UNIQUE_VALUES,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__G_VALUE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_SUMMARY__G_VALUE_RANGE,
      type: summaryTypesNames.RANGE,
    },
    {
      aggregateColumn: columnsAPINamesEnums.TECHNICAL_DATA__U_VALUE,
      name: columnsAPINamesEnums.TECHNICAL_DATA__WINDOW_SUMMARY__U_VALUE_RANGE,
      type: summaryTypesNames.RANGE,
    },
  ];

  const filteredData = data?.filter(
    (row) => row[columnsAPINamesEnums.TECHNICAL_DATA__SUB_COMPONENT] === techDataSections.WINDOWS
  );
  return summarizeChartData({ data: filteredData, summaryItems: windowSummaryItems });
};
